import type {
    GeneratedInsight,
    Status,
} from "@amzn/d16-g-client-deck-builder-typescript-client";
import {
    AiEnhancedAssetType,
    InsightOutputFormat,
    InsightType,
} from "@amzn/d16-g-client-deck-builder-typescript-client";
import { useAsync } from "@react-hookz/web";
import { isEmpty } from "lodash";
import React, { useCallback, useMemo } from "react";
import { useStore } from "zustand";

import { getCdbApiClient } from "../../../../../api/cdbApi/cdbApi";
import { useAsyncErrorStatusNotification } from "../../../../../hooks/notifications/useAsyncErrorStatusNotification";
import { authStore } from "../../../../../store/auth";
import { FeatureFlagsStore } from "../../../../../store/featureFlags";
import { discoveryFilterStore } from "../../../../../store/filters";
import { globalWorkbenchStore } from "../../../../../store/globalWorkbench";
import { reportIssueStore } from "../../../../../store/reportIssue";
import { AquilaThumbsFeedbackPlacement } from "../../../../../types/aquila";
import type { CardDetail } from "../../../../../types/card";
import type { DownloadAsset } from "../../../../../types/common";
import type { PackagedInsightAiMetadataAttributes } from "../../../../../types/packagedInsight";
import { PackagedInsightAiDeckSize } from "../../../../../types/packagedInsight";
import { Progress } from "../../../../../types/progress";
import { shortenedInsightTypeLookup } from "../../../../../utils/aiEnhancedInsights.utils";
import { isLoading } from "../../../../../utils/api.utils";
import { isSuccess } from "../../../../../utils/generatedInsights.utils";
import { logViewInsightDetailsClick } from "../../../../../utils/telemetry.utils";
import { AquilaThumbsFeedback } from "../../../../aquilaThumbsFeedback/AquilaThumbsFeedback";
import { useSetForgeMFCPackagedInsightDetails } from "../../../../forgeVisualize/useSetForgeMFCPackagedInsightDetails";
import { useSetForgePackagedInsightDetails } from "../../../../forgeVisualize/useSetForgePackagedInsightDetails";
import { StyledActionsContainer } from "../../../commonStyles";
import { InsightSelectionActionText } from "../../InsightSelectionActionText";
import { ArtifactGenerationFailedAction } from "../ArtifactGenerationFailedAction";
import { PkgVisualizationFailedAction } from "../PkgVisualizationFailedAction";
import { VisualizationSuccessAction } from "../VisualizationSuccessAction";

import { useSetPackagedInsightDetails } from "./useSetPackagedInsightDetails";

export interface PackagedInsightCardActionProps {
    executionId: string;
    advertiserId: string;
    title: string;
    details: CardDetail[];
    progress: Progress;
    description?: string;
    aiEnhancedStatus?: Status;
    index?: number;
    insight: GeneratedInsight;
    aiMetadata?: Record<PackagedInsightAiMetadataAttributes, string>;
}

export const PackagedInsightCardAction: React.FunctionComponent<
    PackagedInsightCardActionProps
> = ({
    executionId,
    advertiserId,
    progress,
    title,
    details,
    description,
    aiEnhancedStatus,
    index,
    insight,
    aiMetadata,
}) => {
    const [contentSubType, filters] = useStore(
        discoveryFilterStore,
        (state) => [state.getContentSubType(), state.getFilters()],
    );
    const insightsData = filters[contentSubType.provider].data;
    const [useUnifiedSearch, thumbsFeedbackCard] = useStore(
        FeatureFlagsStore,
        (state) => [state.unifiedSearch, state.thumbsFeedbackCard],
    );

    const [getSlidesSelected] = useStore(globalWorkbenchStore, (state) => [
        state.getSlidesSelected,
    ]);

    const [setReportIssueContextData] = useStore(reportIssueStore, (state) => [
        state.setReportIssueContextData,
    ]);

    const [previewAsset, setPreviewAsset] = React.useState<
        DownloadAsset | undefined
    >(undefined);
    const [downloadAsset, setDownloadAsset] = React.useState<
        DownloadAsset | undefined
    >(undefined);

    const [requesterAlias] = useStore(authStore, (state) => [state.username]);

    const aiDeckLength = aiMetadata?.deckSize;

    const identifier: string =
        isSuccess(aiEnhancedStatus) &&
        aiDeckLength !== PackagedInsightAiDeckSize.FULL_LENGTH
            ? `shortened-${executionId}`
            : executionId;

    const sourceInsightType: InsightType = InsightType.IHUB;
    const insightType: InsightType = isSuccess(aiEnhancedStatus)
        ? shortenedInsightTypeLookup[sourceInsightType]
        : sourceInsightType;

    const disabled = Object.values({
        executionId,
        advertiserId,
    }).some((val) => isEmpty(val));

    const hasRelevantInsightsDeck: boolean = useMemo(() => {
        return downloadAsset?.hasRelevantInsightsDeck ?? false;
    }, [downloadAsset]);

    const [downloadState, downloadAction] = useAsync(async () => {
        const data = await (
            await getCdbApiClient()
        ).getGeneratedInsightDownload({
            requesterAlias,
            executionId,
            advertiserId,
            aiEnhancedAssetType: isSuccess(aiEnhancedStatus)
                ? AiEnhancedAssetType.CUSTOMIZED_PACKAGE_DECK
                : undefined,
        });
        setDownloadAsset(data as DownloadAsset);
    });
    useAsyncErrorStatusNotification(downloadState, [], {
        Error: "Your insight failed to download. If issue persists, please Report an issue",
    });

    const [previewState, previewAction] = useAsync(async () => {
        const data = await (
            await getCdbApiClient()
        ).getGeneratedInsightDownload({
            requesterAlias,
            executionId,
            advertiserId,
            outputFormat: InsightOutputFormat.PDF,
            aiEnhancedAssetType: isSuccess(aiEnhancedStatus)
                ? AiEnhancedAssetType.CUSTOMIZED_PACKAGE_DECK
                : undefined,
        });
        setPreviewAsset(data as DownloadAsset);
    });
    useAsyncErrorStatusNotification(previewState, [], {
        Error: "Your insight failed to preview. If issue persists, please Report an issue",
    });

    const isForgeEnabled = insight.forgeEnabled;

    const handleInsightDetails = useCallback(async (): Promise<void> => {
        logViewInsightDetailsClick(
            identifier,
            (index ?? 0) + 1,
            insightsData.length,
            insightType,
            useUnifiedSearch,
        );

        if (isForgeEnabled) {
            void downloadAction.execute();
            setReportIssueContextData({ ...insight, executionId });
            setForgePackagedInsightDetails();
        } else {
            void previewAction.execute();
            void downloadAction.execute();
            setReportIssueContextData({ ...insight, executionId });
            setPackagedInsightDetails();
        }
    }, [insightsData, identifier, index, executionId, insight, isForgeEnabled]);

    const setPackagedInsightDetails = useSetPackagedInsightDetails({
        executionId,
        identifier,
        insightType,
        sourceInsightType,
        insight,
        description,
        details,
        title,
        previewAsset,
        downloadAsset,
        previewLoading: isLoading(previewState.status),
        downloadLoading: isLoading(downloadState.status),
        requesterAlias,
        advertiserId,
        aiEnhancedStatus,
        hasRelevantInsightsDeck,
        reset: () => {
            setPreviewAsset(undefined);
            setDownloadAsset(undefined);
        },
    });

    const setForgeMFCPackagedInsightDetails =
        useSetForgeMFCPackagedInsightDetails({
            identifier,
            insightType,
            insight,
            details,
            title,
            downloadLoading: isLoading(downloadState.status),
            reset: () => {
                setDownloadAsset(undefined);
            },
        });

    const setForgePackagedInsightDetails = useSetForgePackagedInsightDetails({
        identifier,
        insightType,
        insight,
        details,
        title,
        downloadLoading: isLoading(downloadState.status),
        reset: () => {
            setDownloadAsset(undefined);
        },
    });

    const numSelectedSlides = getSlidesSelected(identifier).length;

    return (
        <StyledActionsContainer>
            {(() => {
                switch (progress) {
                    case Progress.ARTIFACTS_GENERATION_FAILED:
                        return (
                            <ArtifactGenerationFailedAction
                                executionId={executionId}
                            />
                        );
                    case Progress.VISUALIZATION_FAILED:
                        return (
                            <PkgVisualizationFailedAction
                                executionId={executionId}
                            />
                        );
                    default:
                        return (
                            <VisualizationSuccessAction
                                executionId={executionId}
                                disabled={disabled}
                                handleInsightDetails={handleInsightDetails}
                            />
                        );
                }
            })()}
            {numSelectedSlides > 0 && (
                <InsightSelectionActionText
                    identifier={identifier}
                    numSelectedSlides={numSelectedSlides}
                />
            )}
            {numSelectedSlides === 0 && thumbsFeedbackCard && (
                <AquilaThumbsFeedback
                    identifier={identifier}
                    insightType={insightType}
                    placement={AquilaThumbsFeedbackPlacement.CARD}
                />
            )}
        </StyledActionsContainer>
    );
};
